import { isEmpty, truncate } from "lodash";
import { Button, Card, Image, OverlayTrigger } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { BiCommentDetail, BiLinkExternal, BiTimeFive } from "react-icons/bi";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import ShowMoreText from "react-show-more-text";
import { Post as IPost, ePostType } from "../../@types/Posts";
import { User } from "../../@types/Users";
import { encodePairId, getTimeDiferenceTitle } from "../../utils";
import AssetDetailsHover from "../AssetDetailsHover";
import { getStoriesImageBackground } from "../StoriesNewsData";
import { AddNewComment } from "./AddNewComment";
import { getPostHighlightBackground } from "./backgrounds/getBackground";
import ReactionButton from "./LikeButton";
import { InteractionButtonStyle } from "./style";
import { useState, useEffect } from "react";
import usePostContext from "../../context/PostContext";
import { Comments } from "./Comments";
import { eInteraction } from "../../@types/Interaction";
import OverviewInteractions from "./OverviewInteractions";
import useIcons from "../../hooks/useIcons";

interface Props {
  post: IPost;
  user: User;
}

export function Post({ post, user }: Props) {
  const [showAddComment, setShowAddComment] = useState<boolean>(false);
  const { name, icon } = post.asset_details;
  const background: any = getStoriesImageBackground(post.asset_details.type);
  const interactionIconSize = 18;
  const { interactions, setInteractions } = usePostContext();
  const { getURI } = useIcons();

  const handleAddNewCommentClick = () => setShowAddComment(!showAddComment);
  const commentaries = interactions.filter(
    (interaction) => interaction.type === eInteraction.COMMENT
  );

  useEffect(() => {
    setInteractions(post.interactions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getImageStyle() {
    if (isMobile) {
      return {
        maxHeight: "350px",
        height: "auto",
        width: "100%",
      };
    } else {
      return {
        maxHeight: "450px",
        height: "auto",
        width: "100%",
      };
    }
  }

  function getPostImageSrc() {
    const { type, image } = post;

    if (image) {
      return image;
    } else if (type === ePostType.news) {
      return background.image;
    } else if (type === ePostType.highlight) {
      return getPostHighlightBackground();
    }
  }

  return (
    <div
      style={{
        justifyContent: "center",
        display: "flex",
      }}
    >
      <Card
        className={isMobile ? "mb-4 wd-100p" : "mb-4 wd-60p"}
        style={{ borderRadius: 15 }}
      >
        <Card.Header
          style={{
            borderTopLeftRadius: 15,
            borderTopRightRadius: 15,
          }}
        >
          <Link
            style={{ color: "var(--text-2" }}
            to={`/analysis/${encodePairId(String(post.asset_details.id))}`}
          >
            <div>
              <OverlayTrigger
                delay={{ show: 100, hide: 100 }}
                overlay={AssetDetailsHover(post.asset_details)}
                placement="auto"
              >
                <div style={{ display: "flex" }}>
                  <Image
                    src={getURI(icon)}
                    className="mg-r-10 img-xs rounded-circle"
                  />

                  <p className="tx-medium mg-t-8 tx-16">
                    {truncate(name, {
                      length: isMobile ? 30 : 45,
                      omission: "...",
                    })}
                  </p>
                </div>
              </OverlayTrigger>
            </div>
          </Link>
          <span className="tx-11 pd-l-5">
            Há {getTimeDiferenceTitle(post.created_at)}
            <BiTimeFive className="mg-b-1 mg-l-1" />
          </span>
        </Card.Header>

        <Card.Body>
          <div className="mb-3">
            <ShowMoreText
              lines={3}
              more="Ler mais"
              less="Ler menos"
              expanded={false}
              truncatedEndingComponent={"..."}
            >
              {post.content}
            </ShowMoreText>
          </div>

          {post.image !== "none" && (
            <Image
              src={getPostImageSrc()}
              style={{ ...getImageStyle(), objectFit: "cover" }}
              rounded
              className="mg-t-10"
            />
          )}

          {<OverviewInteractions />}
        </Card.Body>
        <Card.Footer
          style={{
            borderBottomLeftRadius: 15,
            borderBottomRightRadius: 15,
          }}
        >
          <div className="display-flex">
            <ReactionButton post={post} user={user} />
            <Button
              className="mg-l-20"
              style={InteractionButtonStyle}
              variant="light"
              onClick={() => handleAddNewCommentClick()}
            >
              Comentar <BiCommentDetail size={interactionIconSize} />
            </Button>
            {post.url && (
              <Button
                href={post.url}
                target="_blank"
                className="float-right"
                style={InteractionButtonStyle}
                variant="light"
              >
                <BiLinkExternal size={interactionIconSize} />
              </Button>
            )}
          </div>
        </Card.Footer>

        <AddNewComment post={post} user={user} show={showAddComment} />

        {!isEmpty(commentaries) && <Comments post={post} user={user} />}
      </Card>
    </div>
  );
}

export function PostLoadingSkeleton() {
  return (
    <div
      style={{
        justifyContent: "center",
        display: "flex",
      }}
    >
      <Card
        className={"mb-4 wd-100p mw-sm-80p wd-md-70p wd-lg-50p"}
        style={{ borderRadius: 15 }}
      >
        <Card.Header
          style={{
            borderTopLeftRadius: 15,
            borderTopRightRadius: 15,
          }}
        >
          <div className="d-flex">
            <Skeleton height={50} width={50} borderRadius={50} />
            <p className="mg-l-20 mg-t-10">
              <Skeleton height={20} width={150} />
            </p>
          </div>
        </Card.Header>

        <Card.Body>
          <Skeleton height={400} borderRadius={15} />
        </Card.Body>
      </Card>
    </div>
  );
}
